import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    FileText,
    TrendingUp,
    Users,
    Clock,
    Download,
    Calendar as CalendarIcon,
    Filter,
    Loader2,
    DollarSign,
    UserPlus,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react';
import { gymApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { exportToExcel } from '@/lib/export-utils';

export default function ReportsPage() {
    const { gym } = useAuth();
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [reportData, setReportData] = useState<any>(null);
    const [segmentation, setSegmentation] = useState<any>(null);
    const [nutritionStats, setNutritionStats] = useState<any>(null);
    const [dateRange, setDateRange] = useState({
        start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });

    const hasNutrition = (gym?.modules_enabled || []).includes('nutricion');

    const fetchSegmentation = async () => {
        if (!gym?.gym_id) return;
        try {
            const [segRes, nutRes] = await Promise.all([
                gymApi.getClientSegmentation(gym.gym_id),
                hasNutrition ? gymApi.getNutritionAppointmentStats(gym.gym_id) : Promise.resolve(null),
            ]);
            if (segRes.success) setSegmentation(segRes);
            if (nutRes && (nutRes as any).success) setNutritionStats(nutRes);
        } catch (error) {
            console.error('Failed to fetch segmentation:', error);
        }
    };

    const fetchReport = async () => {
        setIsLoading(true);
        try {
            const res = await gymApi.getReportsData(
                dateRange.start,
                dateRange.end,
                null,
                gym?.gym_id || undefined
            );
            if (res.success) {
                setReportData(res.data);
            }
        } catch (error) {
            console.error('Failed to fetch reports:', error);
            toast({
                title: 'Error',
                description: 'No se pudieron cargar los reportes',
                variant: 'destructive'
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        fetchSegmentation();
    }, [gym?.gym_id]);

    const attendanceByWeekday = reportData?.attendanceByWeekday || [];
    const attendanceMax = Math.max(1, ...attendanceByWeekday.map((d: any) => d.count));

    const handleExport = async () => {
        if (!gym?.gym_id || isExporting) return;

        setIsExporting(true);
        toast({
            title: 'Preparando exportación',
            description: 'Estamos recopilando todos los datos de tu gimnasio...',
        });

        try {
            const res = await gymApi.getExportData(gym.gym_id);

            if (res.success) {
                await exportToExcel([
                    { sheetName: 'Clientes', data: res.data.clients },
                    { sheetName: 'Membresías', data: res.data.memberships },
                    { sheetName: 'Ventas', data: res.data.sales },
                    { sheetName: 'Gastos', data: res.data.expenses },
                    { sheetName: 'Asistencias', data: res.data.checkins }
                ], `Reporte_${gym.name.replace(/\s+/g, '_')}`);

                toast({
                    title: '¡Éxito!',
                    description: 'Tu reporte se ha descargado correctamente.',
                });
            }
        } catch (error) {
            console.error('Export failed:', error);
            toast({
                title: 'Error en la exportación',
                description: 'No se pudieron generar los datos para el reporte.',
                variant: 'destructive'
            });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <DashboardLayout
            title="Reportes y Estadísticas"
            subtitle="Analiza el rendimiento y crecimiento de tu gimnasio"
            actions={
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleExport} disabled={isExporting}>
                        {isExporting ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Download className="h-4 w-4 mr-2" />
                        )}
                        {isExporting ? 'Generando...' : 'Exportar Excel'}
                    </Button>
                </div>
            }
        >
            <div className="space-y-6">
                {/* Filters */}
                <Card className="glass-card">
                    <CardContent className="p-4">
                        <div className="flex flex-wrap items-end gap-4">
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Fecha Inicio</label>
                                <div className="relative">
                                    <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="date"
                                        value={dateRange.start}
                                        onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                                        className="pl-10 w-[200px]"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Fecha Fin</label>
                                <div className="relative">
                                    <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="date"
                                        value={dateRange.end}
                                        onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                                        className="pl-10 w-[200px]"
                                    />
                                </div>
                            </div>
                            <Button onClick={fetchReport} className="bg-primary hover:bg-primary/90">
                                <Filter className="h-4 w-4 mr-2" />
                                Filtrar Datos
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {isLoading ? (
                    <div className="flex items-center justify-center h-[40vh]">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <>
                        {/* KPI Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <Card className="glass-card relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <DollarSign className="h-12 w-12 text-primary" />
                                </div>
                                <CardHeader className="pb-2">
                                    <CardDescription className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Ingresos Totales</CardDescription>
                                    <CardTitle className="text-3xl font-black text-primary">
                                        ${reportData?.revenue?.toLocaleString() || '0'}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {reportData?.revenueGrowth !== null && reportData?.revenueGrowth !== undefined ? (
                                        <div className={cn(
                                            "flex items-center text-xs font-medium w-fit px-2 py-1 rounded-full",
                                            reportData.revenueGrowth >= 0 ? "text-green-500 bg-green-500/10" : "text-destructive bg-destructive/10"
                                        )}>
                                            {reportData.revenueGrowth >= 0 ? (
                                                <ArrowUpRight className="h-3 w-3 mr-1" />
                                            ) : (
                                                <ArrowDownRight className="h-3 w-3 mr-1" />
                                            )}
                                            {Math.abs(reportData.revenueGrowth)}% vs período anterior
                                        </div>
                                    ) : (
                                        <div className="flex items-center text-xs text-muted-foreground font-medium bg-muted w-fit px-2 py-1 rounded-full">
                                            Sin comparación disponible
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="glass-card relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <Users className="h-12 w-12 text-blue-500" />
                                </div>
                                <CardHeader className="pb-2">
                                    <CardDescription className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Asistencia Total</CardDescription>
                                    <CardTitle className="text-3xl font-black text-foreground">
                                        {reportData?.attendance || 0}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center text-xs text-muted-foreground font-medium bg-muted w-fit px-2 py-1 rounded-full">
                                        Check-ins validados
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="glass-card relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <UserPlus className="h-12 w-12 text-purple-500" />
                                </div>
                                <CardHeader className="pb-2">
                                    <CardDescription className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Nuevos Clientes</CardDescription>
                                    <CardTitle className="text-3xl font-black text-foreground">
                                        {reportData?.newClients || 0}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {reportData?.newClientsGrowth !== null && reportData?.newClientsGrowth !== undefined ? (
                                        <div className={cn(
                                            "flex items-center text-xs font-medium w-fit px-2 py-1 rounded-full",
                                            reportData.newClientsGrowth >= 0 ? "text-green-500 bg-green-500/10" : "text-destructive bg-destructive/10"
                                        )}>
                                            {reportData.newClientsGrowth >= 0 ? (
                                                <ArrowUpRight className="h-3 w-3 mr-1" />
                                            ) : (
                                                <ArrowDownRight className="h-3 w-3 mr-1" />
                                            )}
                                            {Math.abs(reportData.newClientsGrowth)}% vs período anterior
                                        </div>
                                    ) : (
                                        <div className="flex items-center text-xs text-muted-foreground font-medium bg-muted w-fit px-2 py-1 rounded-full">
                                            {reportData?.newClients || 0} en el período
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="glass-card relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <Clock className="h-12 w-12 text-orange-500" />
                                </div>
                                <CardHeader className="pb-2">
                                    <CardDescription className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Accesos Denegados</CardDescription>
                                    <CardTitle className="text-3xl font-black text-destructive">
                                        {reportData?.denied || 0}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center text-xs text-destructive font-medium bg-destructive/10 w-fit px-2 py-1 rounded-full">
                                        Membresías vencidas/faltantes
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Detailed Sections */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Revenue Summary */}
                            <Card className="glass-card border-l-4 border-l-primary">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <TrendingUp className="h-5 w-5 text-primary" />
                                        Resumen de Ingresos
                                    </CardTitle>
                                    <CardDescription>Desglose de pagos recibidos en el periodo</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border/50">
                                            <div>
                                                <p className="text-sm font-bold">Membresías Pagadas</p>
                                                <p className="text-xs text-muted-foreground">Efectivo/Tarjeta</p>
                                            </div>
                                            <p className="text-xl font-black">${Number(reportData?.membershipRevenue || 0).toLocaleString()}</p>
                                        </div>
                                        <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border/50">
                                            <div>
                                                <p className="text-sm font-bold">Ventas (POS)</p>
                                                <p className="text-xs text-muted-foreground">Productos y servicios</p>
                                            </div>
                                            <p className="text-xl font-black">${Number(reportData?.salesRevenue || 0).toLocaleString()}</p>
                                        </div>
                                        <div className="flex items-center justify-between p-4 rounded-xl bg-orange-500/5 border border-orange-500/20">
                                            <div>
                                                <p className="text-sm font-bold text-orange-500">Pagos Pendientes</p>
                                                <p className="text-xs text-muted-foreground">Por recolectar</p>
                                            </div>
                                            <p className="text-xl font-black text-orange-500">${Number(reportData?.pendingRevenue || 0).toLocaleString()}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Attendance Pattern */}
                            <Card className="glass-card border-l-4 border-l-blue-500">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Activity className="h-5 w-5 text-blue-500" />
                                        Patrón de Asistencia
                                    </CardTitle>
                                    <CardDescription>Frecuencia de visitas de clientes</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-[150px] flex items-end gap-2 px-2 pb-2 border-b border-border/30">
                                        {attendanceByWeekday.map((d: any, i: number) => {
                                            const pct = Math.round((d.count / attendanceMax) * 100);
                                            return (
                                                <div
                                                    key={i}
                                                    className="relative flex-1 h-full flex items-end group/bar"
                                                    title={`${d.label}: ${d.count} visitas`}
                                                >
                                                    <div
                                                        className="w-full bg-blue-500/20 hover:bg-blue-500/40 rounded-t-sm transition-all cursor-help relative"
                                                        style={{ height: `${d.count > 0 ? Math.max(pct, 4) : 0}%` }}
                                                    >
                                                        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-muted-foreground opacity-0 group-hover/bar:opacity-100 transition-opacity">
                                                            {d.count}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="flex justify-between mt-2 px-1 text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">
                                        <span>Lun</span>
                                        <span>Mar</span>
                                        <span>Mie</span>
                                        <span>Jue</span>
                                        <span>Vie</span>
                                        <span>Sab</span>
                                        <span>Dom</span>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Segmentación de clientas + citas de nutrición */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card className="glass-card border-l-4 border-l-purple-500">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Users className="h-5 w-5 text-purple-500" />
                                        Clientas activas vs. inactivas
                                    </CardTitle>
                                    <CardDescription>Según membresía vigente</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {segmentation?.success ? (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border/50">
                                                <div>
                                                    <p className="text-sm font-bold">Con membresía vigente</p>
                                                    <p className="text-xs text-muted-foreground">Activas hoy</p>
                                                </div>
                                                <p className="text-2xl font-black text-green-600">
                                                    {segmentation.with_active_membership ?? 0}
                                                </p>
                                            </div>
                                            <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border/50">
                                                <div>
                                                    <p className="text-sm font-bold">Sin membresía vigente</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        Vencidas, pendientes o inactivas
                                                    </p>
                                                </div>
                                                <p className="text-2xl font-black text-destructive">
                                                    {segmentation.without_active_membership ?? 0}
                                                </p>
                                            </div>
                                            <div className="flex flex-wrap gap-2 pt-1">
                                                <Badge variant="secondary">Total: {segmentation.total ?? 0}</Badge>
                                                <Badge variant="success">{segmentation.status_active ?? 0} activas</Badge>
                                                <Badge variant="warning">{segmentation.status_pending ?? 0} pendientes</Badge>
                                                <Badge variant="muted">{segmentation.status_inactive ?? 0} inactivas</Badge>
                                                <Badge variant="destructive">{segmentation.status_expired ?? 0} vencidas</Badge>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="py-6 text-center text-sm text-muted-foreground">
                                            Sin datos de segmentación.
                                        </p>
                                    )}
                                </CardContent>
                            </Card>

                            {hasNutrition && (
                                <Card className="glass-card border-l-4 border-l-orange-500">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <CalendarIcon className="h-5 w-5 text-orange-500" />
                                            Citas de nutrición
                                        </CardTitle>
                                        <CardDescription>Agenda de control corporal</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        {nutritionStats?.success ? (
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="rounded-xl bg-muted/30 border border-border/50 p-4">
                                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider">Hoy</p>
                                                    <p className="text-2xl font-black">{nutritionStats.today ?? 0}</p>
                                                </div>
                                                <div className="rounded-xl bg-muted/30 border border-border/50 p-4">
                                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider">Próximos 7 días</p>
                                                    <p className="text-2xl font-black">{nutritionStats.upcoming_7d ?? 0}</p>
                                                </div>
                                                <div className="rounded-xl bg-muted/30 border border-border/50 p-4">
                                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider">Este mes</p>
                                                    <p className="text-2xl font-black">{nutritionStats.month_total ?? 0}</p>
                                                </div>
                                                <div className="rounded-xl bg-muted/30 border border-border/50 p-4">
                                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider">Reservadas en línea</p>
                                                    <p className="text-2xl font-black">{nutritionStats.month_from_public ?? 0}</p>
                                                </div>
                                                {(nutritionStats.month_no_show ?? 0) > 0 && (
                                                    <div className="col-span-2 rounded-xl bg-destructive/5 border border-destructive/20 p-4">
                                                        <p className="text-xs text-destructive uppercase font-bold tracking-wider">
                                                            Inasistencias este mes
                                                        </p>
                                                        <p className="text-2xl font-black text-destructive">
                                                            {nutritionStats.month_no_show}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="py-6 text-center text-sm text-muted-foreground">
                                                Sin datos de citas.
                                            </p>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    </>
                )}
            </div>
        </DashboardLayout>
    );
}

// Internal Activity Icon for Reports
function Activity({ className }: { className?: string }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
    );
}
